/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DispatchLocationsPage.tsx
 🗓️ Created: 2025-12-04
 🧩 Description:
    Dispatch Locations list page with DataGrid
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, MapPin, CheckCircle, XCircle } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import Toggle from '../../components/ui/Toggle';
import { useDispatchLocationList } from './hooks/useDispatchLocationList';
import { deleteDispatchLocation } from './api/dispatchLocationApi';
import type { DispatchLocation } from './types';

export default function DispatchLocationsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    location: DispatchLocation | null;
  }>({
    isOpen: false,
    location: null,
  });

  // Define columns
  const availableColumns = useMemo(
    () => [
      {
        key: 'locationName',
        label: t('dispatchLocations:columns.locationName'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: DispatchLocation) => (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-blue-600" />
            <div>
              <div className="font-medium text-foreground">{item.locationName}</div>
              {item.locationCode && (
                <div className="text-xs text-muted-foreground font-mono">{item.locationCode}</div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'assignment',
        label: t('dispatchLocations:columns.assignment'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: DispatchLocation) => {
          const colors = {
            user: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
            device: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
            site: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            global: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
          };
          return (
            <div>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${colors[item.assignmentType]}`}
              >
                {t(`dispatchLocations:assignmentTypes.${item.assignmentType}`)}
              </span>
              {item.extensionNumber && (
                <div className="text-xs text-muted-foreground mt-1">
                  Ext: {item.extensionNumber}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'address',
        label: t('dispatchLocations:columns.address'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: DispatchLocation) => (
          <div className="text-sm">
            <div className="text-foreground">
              {[item.streetNumber, item.streetName, item.streetSuffix]
                .filter(Boolean)
                .join(' ') || '-'}
            </div>
            {item.city && (
              <div className="text-xs text-muted-foreground">
                {[item.city, item.state, item.postalCode].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'building',
        label: t('dispatchLocations:columns.building'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: DispatchLocation) => (
          <div className="text-sm">
            {item.buildingName && (
              <div className="text-foreground">{item.buildingName}</div>
            )}
            {(item.floor || item.room) && (
              <div className="text-xs text-muted-foreground">
                {[item.floor && `Floor ${item.floor}`, item.room && `Room ${item.room}`]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            )}
            {!item.buildingName && !item.floor && !item.room && (
              <span className="text-muted-foreground">-</span>
            )}
          </div>
        ),
      },
      {
        key: 'elin',
        label: t('dispatchLocations:columns.elin'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: DispatchLocation) => (
          <span className="font-mono text-sm text-foreground">{item.elin || '-'}</span>
        ),
      },
      {
        key: 'validated',
        label: t('dispatchLocations:columns.validated'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: DispatchLocation) => (
          <div className="flex items-center gap-1">
            {item.validated ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                <CheckCircle className="h-3 w-3" />
                {t('dispatchLocations:validated')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                <XCircle className="h-3 w-3" />
                {t('dispatchLocations:notValidated')}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'enabled',
        label: t('dispatchLocations:columns.status'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: DispatchLocation) => (
          <Toggle value={item.enabled} onChange={() => {}} disabled className="pointer-events-none" />
        ),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        sortable: false,
        visible: true,
      },
    ],
    [t]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDispatchLocationList({
    initialColumns: availableColumns,
    initialSortBy: 'locationName',
    initialSortOrder: 'asc',
    initialLimit: 10,
  });

  // Handle delete
  const handleDelete = useCallback(
    async (location: DispatchLocation) => {
      try {
        await deleteDispatchLocation(location.id);
        showSuccess(t('dispatchLocations:messages.deleteSuccess'));
        setDeleteModal({ isOpen: false, location: null });
        refresh();
      } catch (error) {
        const msg = error instanceof Error ? error.message : t('dispatchLocations:messages.deleteError');
        showError(msg);
      }
    },
    [showSuccess, showError, t, refresh]
  );

  // Render actions
  const renderActions = useCallback(
    (item: DispatchLocation) => (
      <div className="flex items-center justify-end gap-1">
        <SimpleTooltip content={t('dispatchLocations:actions.edit')} position="top">
          <button
            onClick={() => navigate(`/pbx/emergency/locations/${item.uuid ?? item.id}`)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Pencil className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('dispatchLocations:actions.delete')} position="top">
          <button
            onClick={() => setDeleteModal({ isOpen: true, location: item })}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [navigate, t]
  );

  return (
    <div className="p-6">
      <DataGridSimple
        data={data}
        loading={loading}
        columns={columns}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        renderActions={renderActions}
        searchPlaceholder={t('dispatchLocations:searchPlaceholder')}
        title={t('dispatchLocations:title')}
        titleIcon={<MapPin className="h-5 w-5 text-blue-600" />}
        description={t('dispatchLocations:subtitle')}
        addButtonText={t('dispatchLocations:actions.add')}
        onAdd={() => navigate('/pbx/emergency/locations/new')}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, location: null })}
        onConfirm={() => deleteModal.location && handleDelete(deleteModal.location)}
        title={t('dispatchLocations:deleteModal.title')}
        message={t('dispatchLocations:deleteModal.message', {
          location: deleteModal.location?.locationName || '',
        })}
        confirmText={t('dispatchLocations:deleteModal.confirm')}
        cancelText={t('dispatchLocations:deleteModal.cancel')}
      />
    </div>
  );
}

